/* ──────────────────────────────────────────────────────────
   Right-rail sidebar components for the user dashboard:

   - WelcomeModal     First-login welcome with a short preview
                      of the journey ahead. Self-dismisses; the
                      server records welcomeSeenAt so it doesn't
                      reappear.
   - NewUserChecklist Three-step guide derived from data the
                      dashboard already loads. Auto-collapses to
                      a single celebratory line once all three
                      items are done.
   - UpgradeCard      Discoverable-but-quiet billing-portal entry
                      for active members.
   ────────────────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";

const COL = {
  bg: "#111111",
  bgSecondary: "#171717",
  glass: "rgba(255,255,255,0.06)",
  glassStrong: "rgba(255,255,255,0.10)",
  glassHover: "rgba(255,255,255,0.14)",
  borderSubtle: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.14)",
  text: "#F5F5F5",
  textSecondary: "rgba(255,255,255,0.72)",
  textMuted: "rgba(255,255,255,0.48)",
  textDisabled: "rgba(255,255,255,0.28)",
  accent: "#E5D3A3",
  accentGreen: "#83F28F",
  innerGlow: "inset 0 1px 0 rgba(255,255,255,0.08)",
};

/* ─── Welcome modal — first-login only ─────────────────── */
// Surfaces on first sign-in only. We show a short preview of what
// the dashboard will help the user do, so the first impression is
// "I see what's possible," not "what is all this?"
export function WelcomeModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user && !user.welcomeSeenAt) setOpen(true);
  }, [user]);

  if (!open || !user) return null;

  const dismiss = async () => {
    setOpen(false);
    await api.post("/me/welcome-seen").catch(() => {});
    refresh();
  };

  const previewSteps = [
    { num: "01", label: "See your roadmap",          note: "A timeline of where we're headed together." },
    { num: "02", label: "Activate your membership",  note: "Choose the plan that fits your stage." },
    { num: "03", label: "Tell us about your project", note: "Quick questionnaire to align on goals." },
  ];

  return (
    <div style={M.overlay} onClick={dismiss}>
      <div style={M.box} onClick={(e) => e.stopPropagation()}>
        <p style={M.eyebrow}>◈ &nbsp; WELCOME</p>
        <h2 style={M.h}>Hello, <em style={{ fontStyle: "italic", color: COL.mid }}>{user.firstName}.</em></h2>
        <p style={M.body}>
          You're in. This is your home base — your roadmap, projects, and
          conversations all live here. Here's what you'll find on the way.
        </p>

        {/* Preview list — sets expectations, doesn't hide them. */}
        <div style={M.previewList}>
          {previewSteps.map((s) => (
            <div key={s.num} style={M.previewRow}>
              <div style={M.previewNum}>{s.num}</div>
              <div>
                <div style={M.previewLabel}>{s.label}</div>
                <div style={M.previewNote}>{s.note}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={M.actions}>
          <button onClick={dismiss} style={M.btn}>
            <span>Take a look around</span><span>→</span>
          </button>
          <button onClick={dismiss} style={M.btnQuiet}>
            <span>Skip the intro</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── New User Checklist ───────────────────────────────── */
// Steps are derived from data the dashboard already loads:
//   1. Activate Your Membership  → hasActiveAccess
//   2. Vision Questionnaire      → intake exists
//   3. Schedule Your Kick-Off    → at least one upcoming meeting
//
// Once all three are complete, the card collapses to a single
// celebratory line rather than continuing to take up the right
// rail with a fully-checked list.
export function NewUserChecklist({ hasActiveAccess, hasIntake, hasMeeting }) {
  const items = [
    { label: "Activate Your Membership",           note: "Choose a plan that fits where you are today.", done: hasActiveAccess },
    { label: "Complete Your Vision Questionnaire", note: "Tell us about your goals and your team.",       done: hasIntake,  locked: !hasActiveAccess },
    { label: "Schedule Your Kick-Off",             note: "Pick a time that works for our first meeting.", done: hasMeeting, locked: !hasActiveAccess },
  ];

  const completed = items.filter((i) => i.done).length;
  const allDone   = completed === items.length;

  if (allDone) {
    // Collapsed celebration state — keeps the rail tidy and gives
    // the user a quiet acknowledgement.
    return (
      <div style={CL.doneRoot}>
        <div style={CL.doneBadge}>✓</div>
        <div>
          <div style={CL.doneLabel}>You're set up.</div>
          <div style={CL.doneNote}>Onboarding complete — we're ready when you are.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={CL.root}>
      <div style={CL.head}>
        <span style={CL.eyebrow}>◈ &nbsp; YOUR FIRST STEPS</span>
        <span style={CL.count}>{completed} / {items.length}</span>
      </div>

      {/* Thin progress bar above the list — quietly reinforces
          that progress is happening. */}
      <div style={CL.barOuter} aria-hidden="true">
        <div style={{ ...CL.barInner, width: `${Math.round((completed / items.length) * 100)}%` }} />
      </div>

      <div style={CL.list}>
        {items.map((it, i) => (
          <div key={i} style={CL.item}>
            <div style={{
              ...CL.check,
              ...(it.done ? CL.checkDone : it.locked ? CL.checkLocked : {}),
            }}>
              {it.done ? "✓" : it.locked ? "◌" : String(i + 1).padStart(2, "0")}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                ...CL.label,
                textDecoration: it.done ? "line-through" : "none",
                color: it.done ? COL.muted : COL.ink,
              }}>{it.label}</div>
              <div style={CL.note}>{it.note}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Upgrade card — opens Stripe billing portal ──────── */
// "Discoverable but not super obvious, clear within 1 click" per spec.
export function UpgradeCard() {
  const { hasActiveAccess } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!hasActiveAccess) return null;

  const open = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/portal");
      if (r.data.url) window.location.href = r.data.url;
    } catch { setBusy(false); }
  };

  return (
    <div style={UC.root}>
      <div style={UC.eyebrow}>◌ &nbsp; YOUR PLAN</div>
      <div style={UC.label}>Manage your membership.</div>
      <p style={UC.body}>Update your plan, payment method, or billing details.</p>
      <button onClick={open} disabled={busy} style={UC.btn}>
        <span>{busy ? "Opening…" : "Open billing portal"}</span><span>→</span>
      </button>
    </div>
  );
}

/* ─── Styles ───────────────────────────────────────────── */
const M = {
  overlay: { position: "fixed", inset: 0, zIndex: 80, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  box: {
    background: "#171717",
    border: `1px solid rgba(255,255,255,0.12)`,
    borderRadius: 24,
    padding: "40px 36px 32px",
    maxWidth: 560,
    width: "100%",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.28em", color: COL.textMuted, marginBottom: 16, textTransform: "uppercase" },
  h: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 300, fontSize: 36, lineHeight: 1.1, marginBottom: 16, color: COL.text,
  },
  body: { color: COL.textSecondary, fontSize: 14, lineHeight: 1.75, marginBottom: 24 },

  previewList: { display: "flex", flexDirection: "column", gap: 14, padding: "20px 0", borderTop: `1px solid rgba(255,255,255,0.08)`, borderBottom: `1px solid rgba(255,255,255,0.08)`, marginBottom: 24 },
  previewRow:  { display: "flex", gap: 14, alignItems: "flex-start" },
  previewNum:  { fontFamily: "'DM Mono', monospace", fontSize: 11, color: COL.textMuted, width: 28, lineHeight: 1.4, paddingTop: 2 },
  previewLabel:{ fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", fontSize: 15, fontWeight: 500, color: COL.text, marginBottom: 2 },
  previewNote: { fontSize: 12, color: COL.textSecondary, lineHeight: 1.5 },

  actions: { display: "flex", gap: 12, alignItems: "center" },
  btn: {
    padding: "11px 22px",
    fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
    background: "rgba(229,211,163,0.15)", color: COL.accent,
    border: `1px solid rgba(229,211,163,0.3)`,
    borderRadius: 999, cursor: "pointer", display: "inline-flex", gap: 10, alignItems: "center",
  },
  btnQuiet: {
    padding: "11px 16px", fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.18em",
    textTransform: "uppercase", background: "transparent", color: COL.textMuted, border: "none", cursor: "pointer",
  },
};

const CL = {
  root: {
    background: COL.glass,
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    border: `1px solid ${COL.borderSubtle}`,
    borderRadius: 20,
    padding: 20,
    boxShadow: COL.innerGlow,
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: COL.textMuted },
  count: { fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", fontSize: 18, fontWeight: 300, color: COL.text },

  barOuter: { height: 2, background: "rgba(255,255,255,0.08)", borderRadius: 999, position: "relative", marginBottom: 18, overflow: "hidden" },
  barInner: { position: "absolute", left: 0, top: 0, height: "100%", background: "rgba(229,211,163,0.6)", borderRadius: 999, transition: "width 0.5s cubic-bezier(0.22,1,0.36,1)" },

  list: { display: "flex", flexDirection: "column", gap: 14 },
  item: { display: "flex", gap: 12, alignItems: "flex-start" },
  check: {
    width: 24, height: 24, borderRadius: 999,
    border: `1px solid rgba(255,255,255,0.14)`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "'DM Mono', monospace", fontSize: 9, color: COL.textMuted, flexShrink: 0,
  },
  checkDone:   { background: "rgba(131,242,143,0.15)", color: COL.accentGreen, borderColor: "rgba(131,242,143,0.3)" },
  checkLocked: { background: "transparent", color: COL.textDisabled, borderStyle: "dashed" },
  label: { fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 400, color: COL.text, marginBottom: 3 },
  note:  { fontSize: 11, color: COL.textMuted, lineHeight: 1.5 },

  /* All-done collapsed state */
  doneRoot:   {
    background: "rgba(131,242,143,0.08)",
    border: `1px solid rgba(131,242,143,0.2)`,
    borderRadius: 20,
    padding: "18px 20px",
    display: "flex", gap: 12, alignItems: "center",
  },
  doneBadge:  {
    width: 30, height: 30, borderRadius: 999,
    background: "rgba(131,242,143,0.2)", color: COL.accentGreen,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 600, fontSize: 13, flexShrink: 0,
  },
  doneLabel:  { fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", fontSize: 15, fontWeight: 500, color: COL.text, marginBottom: 2 },
  doneNote:   { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: COL.textMuted },
};

const UC = {
  root: {
    background: COL.glass,
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    border: `1px solid ${COL.borderSubtle}`,
    borderRadius: 20,
    padding: 20,
    boxShadow: COL.innerGlow,
  },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: COL.textMuted, marginBottom: 10 },
  label: { fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", fontSize: 16, fontWeight: 400, color: COL.text, marginBottom: 6 },
  body: { fontSize: 12, color: COL.textSecondary, lineHeight: 1.6, marginBottom: 14 },
  btn: {
    padding: "9px 16px",
    fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
    background: "rgba(255,255,255,0.06)", color: COL.text,
    border: `1px solid rgba(255,255,255,0.12)`,
    borderRadius: 999, cursor: "pointer", display: "inline-flex", gap: 10, alignItems: "center",
  },
};
